import { AxiosError } from 'axios'
import { IChat, IChatMessage, IChatProduct, IChatSeller, IChatUser } from 'models/chat'
import { IMAGES_URL } from 'shared/constants/urls'

export const getUserName = (user?: IChatUser) => {
  const fullName = user?.full_name || `${user?.name || ''} ${user?.surname || ''}`.trim()
  return fullName || 'Покупатель'
}

// Бэкенд собирает full_name как «Фамилия Имя Отчество» (models/User.ts),
// поэтому части ФИО берём из него по порядку
export const getUserNameLines = (user?: IChatUser): string[] => {
  const parts = user?.full_name
    ? user.full_name.split(/\s+/).filter(Boolean)
    : [user?.surname, user?.name].filter(Boolean) as string[]

  return parts.length ? parts : ['Покупатель']
}

export const getSellerName = (seller?: IChatSeller | null) =>
  seller?.shop_details?.shop_name || 'Магазин'

// В обращении в поддержку автора называем магазином, если он продавец;
// гость без аккаунта известен только по тому, как представился в виджете
export const getChatAuthorName = (chat?: IChat | null) => {
  if (chat?.support_seller_id) return getSellerName(chat.support_seller_id)
  if (chat?.user_id) return getUserName(chat.user_id)

  return chat?.contact_name || chat?.contact_email || 'Гость'
}

export const getSupportChatSubtitle = (chat: IChat) => {
  const kind = chat.support_seller_id
    ? 'Обращение продавца'
    : chat.guest_id ? 'Обращение гостя' : 'Обращение покупателя'

  return chat.contact_email ? `${kind} · ${chat.contact_email}` : kind
}

export const getProductImage = (product?: IChatProduct | null) => {
  const photo = Array.isArray(product?.photo) ? product?.photo[0] : product?.photo
  if (!photo) return ''
  return /^https?:\/\//.test(photo) ? photo : `${IMAGES_URL}${photo}`
}

export const getChatProduct = (
  chat?: IChat | null,
  messages: IChatMessage[] = []
) => {
  if (chat?.last_product) return chat.last_product
  return [...messages].reverse().find((item) => item.product_id)?.product_id || null
}

export const formatDateTime = (date?: string) => {
  if (!date) return ''
  return new Date(date).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Срок правки назначает сервер: null — без срока; нет поля — сервер правку не принимает
export const isWithinEditWindow = (message: IChatMessage, now: number) =>
  message.editable_until === null ||
  (Boolean(message.editable_until) && Date.parse(message.editable_until as string) > now)

export const replaceMessage = (messages: IChatMessage[], message: IChatMessage) =>
  messages.map((item) => (item._id === message._id ? message : item))

// Отказ сервера (лишнее фото, срок правки вышел) понятен человеку, сбой — нет
export const getRequestErrorText = (e: unknown) => {
  const response = (e as AxiosError<{ message?: unknown }>).response
  return response && response.status < 500 && typeof response.data?.message === 'string'
    ? response.data.message
    : 'Попробуйте ещё раз'
}

// Как в списке диалогов у сервера: сообщение без текста — «Фото» или «Видео»
export const getMessagePreview = (message: Pick<IChatMessage, 'text' | 'images' | 'videos'>) =>
  message.text || (message.images?.length ? 'Фото' : message.videos?.length ? 'Видео' : '')

// Подпись к фото или видео можно стереть: вложение останется сообщением
export const hasMedia = (message: Pick<IChatMessage, 'images' | 'videos'>) =>
  Boolean(message.images?.length || message.videos?.length)

export const getChatImageUrl = (key: string) => `${IMAGES_URL}${key}`
